import clsx from "clsx";
import { useState } from "react";

import EmojiSelector from "common/components/EmojiSelector";
import RenderIf from "common/components/RenderIf";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useConversationMessagesUpdates from "../../../datahooks/useConversationMessagesUpdates";
import { useConversationUrnContext } from "../ConversationUrnContext";

import type { ConversationMessage, MessageReaction } from "../../../types";

import SmileyAdd from "assets/icons/smiley-add.svg?react";

interface ReactionsProps {
  accountId: string;
  message: ConversationMessage;
  reactions: MessageReaction[];
}

const MAX_REACTIONS = 3;

export default function Reactions({
  accountId,
  message,
  reactions,
}: ReactionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const conversationUrn = useConversationUrnContext();
  const { sendMessageReaction } = useConversationMessagesUpdates({
    conversationUrn,
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const isMine = accountId === message.sender?.id;

  function onMessageReact(emoji: string) {
    // whether the user reacted or not
    const { reader_reacted: isReacted } =
      message.reactions?.find((reaction) => reaction.emoji === emoji) || {};

    sendMessageReaction({
      accountId,
      emoji,
      messageUrn: message.urn,
      unreact: isReacted,
    });
  }

  // if desktop or expanded, show all reactions
  const visibleReactions =
    isDesktop || isExpanded ? reactions : reactions?.slice(0, MAX_REACTIONS);

  const hiddenCount =
    reactions?.length && reactions.length > MAX_REACTIONS
      ? reactions.length - MAX_REACTIONS
      : 0;

  return (
    <div
      className={clsx([
        "flex max-w-[23.25rem] flex-row flex-wrap items-center gap-0.5",
        isMine ? "justify-end" : "justify-start",
      ])}
    >
      {visibleReactions?.map(({ emoji, count, reader_reacted }) => (
        <RenderIf key={emoji} condition={!!count}>
          <button
            type="button"
            className="flex h-fit flex-row items-center justify-center gap-2 rounded-full bg-black-100 px-2 py-1"
            onClick={() =>
              sendMessageReaction({
                accountId,
                emoji,
                messageUrn: message.urn,
                unreact: reader_reacted,
              })
            }
          >
            <span className="text-xs">{emoji}</span>
            <span className="text-xs">{count}</span>
          </button>
        </RenderIf>
      ))}

      {/* More reactions button (mobile only) */}
      <RenderIf condition={!isDesktop && hiddenCount > 0}>
        <button
          type="button"
          className="flex h-fit flex-row items-center justify-center gap-2 rounded-full bg-black-100 px-2 py-1 text-xs"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show less" : `+ ${hiddenCount} more`}
        </button>
      </RenderIf>

      {/* emoji selector */}
      <RenderIf condition={message.reactions?.length > 0}>
        <EmojiSelector
          buttonProps={{ children: <SmileyAdd />, size: "xs" }}
          onEmojiSelection={onMessageReact}
        />
      </RenderIf>
    </div>
  );
}
